import { ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SupplierDetailLink } from "@/components/hub/HubEntityDetailLink";
import { EndUserDetailLink } from "@/components/inside-sales/EndUserDetailLink";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn, EM_DASH } from "@/lib/utils";

import {
  CORRECTIVE_ACTION_STATUSES,
  correctiveActionLabel,
  correctiveActionSourceLabel,
  correctiveActionStatusLabel,
} from "./correctiveActionsApi";
import { QUALITY_CASE_STATUSES, qualityCaseStatusLabel } from "./casesApi";
import { RMA_STATUSES, rmaStatusLabel } from "./rmaApi";
import { QualityCaseLink } from "./QualityCaseEventsCard";
import { QualityEventNcrTable } from "./QualityEventNcrTable";
import { QualityEventNotesThread } from "./QualityEventNotesThread";
import { QualityFreshdeskTicketPicker } from "./QualityFreshdeskTicketPicker";
import {
  addQualityEventFreshdeskTicket,
  createQualityCaseForEvent,
  createQualityEventNcr,
  fetchQualityEvent,
  linkQualityEventCase,
  linkQualityEventNcr,
  linkQualityEventRma,
  qualityEventLabel,
  qualityEventQueryKey,
  qualityEventStatusLabel,
  removeQualityEventFreshdeskTicket,
  updateQualityEventStatus,
  type QualityEvent,
  type QualityEventStatus,
} from "./qualityEventPageApi";

const DETAIL_HELP =
  "Source is the category and the linked party on one line. Notes stay on this event. Linked documents groups NCRs, the quality case, RCAs, and RMAs. Each tab shows how many are linked.";

type LinkedDocTab = "ncrs" | "cases" | "rcas" | "cars" | "rmas";

function formatTimestamp(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function errorMessage(error: unknown): string | null {
  if (error == null) return null;
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Request failed.";
}

function positiveId(value: string, label: string): number {
  const trimmed = value.trim();
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 1 || String(parsed) !== trimmed) {
    throw new Error(`${label} must be a positive integer.`);
  }
  return parsed;
}

function sourceCategoryText(event: QualityEvent): string {
  if (event.sourceCategory != null) return correctiveActionSourceLabel(event.sourceCategory);
  return event.source?.trim() ?? "";
}

export function QualityEventDetailPage() {
  const { id } = useParams();
  const eventId = Number(id);
  const navigate = useNavigate();
  const writesAllowed = useHubModuleWrite("quality");
  const queryClient = useQueryClient();
  const eventQuery = useQuery({
    queryKey: qualityEventQueryKey(eventId),
    queryFn: () => fetchQualityEvent(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
  });

  const [ncrId, setNcrId] = useState("");
  const [ncrIssue, setNcrIssue] = useState("");
  const [caseId, setCaseId] = useState("");
  const [caseTitle, setCaseTitle] = useState("");
  const [rmaNumber, setRmaNumber] = useState("");
  const [ticketError, setTicketError] = useState<string | null>(null);

  const replaceEvent = (event: QualityEvent) => {
    queryClient.setQueryData(qualityEventQueryKey(event.id), event);
  };

  const statusMutation = useMutation({
    mutationFn: (status: QualityEventStatus) => updateQualityEventStatus(eventId, status),
    onSuccess: replaceEvent,
  });
  const ncrMutation = useMutation({
    mutationFn: async (input: { mode: "link"; idText: string } | { mode: "create"; issue: string }) => {
      if (input.mode === "link") return linkQualityEventNcr(eventId, positiveId(input.idText, "NCR id"));
      return createQualityEventNcr(eventId, input.issue);
    },
    onSuccess: (event, input) => {
      replaceEvent(event);
      if (input.mode === "link") setNcrId("");
      else setNcrIssue("");
    },
  });
  const caseMutation = useMutation({
    mutationFn: async (input: { mode: "link"; idText: string } | { mode: "create"; title: string }) => {
      const qualityCaseId =
        input.mode === "link"
          ? positiveId(input.idText, "Quality case id")
          : await createQualityCaseForEvent(input.title, eventQuery.data?.sourceCategory ?? null);
      return linkQualityEventCase(eventId, qualityCaseId);
    },
    onSuccess: (event, input) => {
      replaceEvent(event);
      if (input.mode === "link") setCaseId("");
      else setCaseTitle("");
    },
  });
  const rmaLinkMutation = useMutation({
    mutationFn: (rmaText: string) => linkQualityEventRma(eventId, rmaText),
    onSuccess: (event) => {
      replaceEvent(event);
      setRmaNumber("");
    },
  });
  const ticketMutation = useMutation({
    mutationFn: async (ticketIds: number[]) => {
      const event = eventQuery.data;
      if (event == null) throw new Error("Quality event was not found.");
      const current = new Set(event.freshdeskTicketIds ?? []);
      const next = new Set(ticketIds);
      const added = ticketIds.filter((ticketId) => !current.has(ticketId));
      const removed = (event.freshdeskTicketIds ?? []).filter((ticketId) => !next.has(ticketId));
      let latest = event;
      for (const ticketId of added) {
        latest = await addQualityEventFreshdeskTicket(event.id, ticketId);
      }
      for (const ticketId of removed) {
        latest = await removeQualityEventFreshdeskTicket(event.id, ticketId);
      }
      return latest;
    },
    onSuccess: (event) => {
      setTicketError(null);
      replaceEvent(event);
    },
    onError: (error: unknown) => setTicketError(errorMessage(error)),
  });

  if (!Number.isFinite(eventId) || eventId <= 0) {
    return <p className="text-sm text-red-700">Quality event was not found.</p>;
  }
  if (eventQuery.isError) {
    return <p className="text-sm text-red-700">{(eventQuery.error as Error).message}</p>;
  }
  if (eventQuery.isLoading || eventQuery.data == null) {
    return <p className="text-sm text-slate-600">Loading quality event…</p>;
  }

  const event = eventQuery.data;
  const nextStatus: QualityEventStatus = event.status === "open" ? "closed" : "open";
  const customerName = event.customerName?.trim() ?? "";
  const supplierName = event.supplierName?.trim() ?? "";
  const sourceCategory = sourceCategoryText(event);

  return (
    <div className="space-y-4">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <PageTitleWithHelp hoverHint={DETAIL_HELP} breadcrumbLabel={qualityEventLabel(event.id)}>
              {qualityEventLabel(event.id)}
            </PageTitleWithHelp>
            <div className="flex items-center gap-1" role="group" aria-label="Status">
              {(["open", "closed"] as const).map((status, index) => {
                const active = event.status === status;
                return (
                  <span key={status} className="inline-flex items-center gap-1">
                    {index > 0 ? <ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden /> : null}
                    <span
                      className={
                        active
                          ? "rounded-full bg-brand-600 px-2.5 py-0.5 text-xs font-medium text-white"
                          : "rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500"
                      }
                      aria-current={active ? "step" : undefined}
                    >
                      {qualityEventStatusLabel(status)}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
          {writesAllowed ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={statusMutation.isPending}
                onClick={() => statusMutation.mutate(nextStatus)}
              >
                {event.status === "open" ? "Close" : "Reopen"}
              </Button>
            </div>
          ) : null}
        </div>
        {errorMessage(statusMutation.error) ? (
          <p className="text-sm text-red-700">{errorMessage(statusMutation.error)}</p>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Source is the category and the linked party. Status is open or closed. Freshdesk tickets stay on this event.">
            Event
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="space-y-4">
          <dl className="grid gap-3 md:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Source</dt>
              <dd className="text-sm text-slate-900">
                {sourceCategory !== "" ? sourceCategory : null}
                {event.sourceCategory === "customer_complaint" && customerName !== "" ? (
                  <>
                    {sourceCategory !== "" ? " - " : null}
                    <EndUserDetailLink
                      fsAccountId={event.customerFsAccountId}
                      endUserName={customerName}
                    />
                  </>
                ) : event.sourceCategory === "supplier" && supplierName !== "" ? (
                  <>
                    {sourceCategory !== "" ? " - " : null}
                    {event.supplierId != null ? (
                      <SupplierDetailLink supplierId={event.supplierId} supplierName={supplierName} />
                    ) : (
                      supplierName
                    )}
                  </>
                ) : sourceCategory === "" ? (
                  EM_DASH
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Opened</dt>
              <dd className="text-sm text-slate-900">{formatTimestamp(event.createdAt)}</dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Summary</dt>
              <dd className="whitespace-pre-wrap text-sm text-slate-900">{event.summary ?? EM_DASH}</dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Freshdesk tickets</dt>
              <dd className="mt-1.5">
                <QualityFreshdeskTicketPicker
                  id="quality-event-freshdesk"
                  values={event.freshdeskTicketIds ?? []}
                  disabled={ticketMutation.isPending}
                  onChange={(ticketIds) => ticketMutation.mutate(ticketIds)}
                />
                {ticketError ? <p className="mt-1 text-sm text-red-700">{ticketError}</p> : null}
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Append-only notes. Each note keeps the author and the time it was written.">
            Notes
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          <QualityEventNotesThread eventId={event.id} />
        </CardBody>
      </Card>

      <QualityEventLinkedDocuments
        event={event}
        ncrId={ncrId}
        onNcrIdChange={setNcrId}
        onLinkNcr={() => ncrMutation.mutate({ mode: "link", idText: ncrId })}
        ncrIssue={ncrIssue}
        onNcrIssueChange={setNcrIssue}
        onCreateNcr={() => ncrMutation.mutate({ mode: "create", issue: ncrIssue.trim() })}
        ncrBusy={ncrMutation.isPending}
        ncrError={errorMessage(ncrMutation.error)}
        caseId={caseId}
        onCaseIdChange={setCaseId}
        onLinkCase={() => caseMutation.mutate({ mode: "link", idText: caseId })}
        caseTitle={caseTitle}
        onCaseTitleChange={setCaseTitle}
        onCreateCase={() => caseMutation.mutate({ mode: "create", title: caseTitle.trim() })}
        caseBusy={caseMutation.isPending}
        caseError={errorMessage(caseMutation.error)}
        writesAllowed={writesAllowed}
        onOpenRma={() => void navigate(`/quality/rmas/new?qualityEventId=${event.id}`)}
        rmaNumber={rmaNumber}
        onRmaNumberChange={setRmaNumber}
        onLinkRma={() => rmaLinkMutation.mutate(rmaNumber.trim())}
        rmaBusy={rmaLinkMutation.isPending}
        rmaError={errorMessage(rmaLinkMutation.error)}
      />
    </div>
  );
}

function QualityEventLinkedDocuments(props: {
  event: QualityEvent;
  ncrId: string;
  onNcrIdChange: (value: string) => void;
  onLinkNcr: () => void;
  ncrIssue: string;
  onNcrIssueChange: (value: string) => void;
  onCreateNcr: () => void;
  ncrBusy: boolean;
  ncrError: string | null;
  caseId: string;
  onCaseIdChange: (value: string) => void;
  onLinkCase: () => void;
  caseTitle: string;
  onCaseTitleChange: (value: string) => void;
  onCreateCase: () => void;
  caseBusy: boolean;
  caseError: string | null;
  writesAllowed: boolean;
  onOpenRma: () => void;
  rmaNumber: string;
  onRmaNumberChange: (value: string) => void;
  onLinkRma: () => void;
  rmaBusy: boolean;
  rmaError: string | null;
}) {
  const ncrs = props.event.ncrs ?? [];
  const rcas = props.event.rcas ?? [];
  const cars = props.event.correctiveActions ?? [];
  const rmas = props.event.rmas ?? [];
  const caseCount = props.event.qualityCaseId == null ? 0 : 1;
  const tabs: { id: LinkedDocTab; label: string; count: number }[] = [
    { id: "cars", label: "Corrective actions", count: cars.length },
    { id: "ncrs", label: "NCRs", count: ncrs.length },
    { id: "cases", label: "Quality cases", count: caseCount },
    { id: "rcas", label: "RCAs", count: rcas.length },
    { id: "rmas", label: "RMAs", count: rmas.length },
  ];
  const [tab, setTab] = useState<LinkedDocTab>("ncrs");
  const [action, setAction] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Hub records attached to this event. The number on each tab is how many of that type are linked.">
          Linked documents
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px" role="tablist" aria-label="Linked documents">
          {tabs.map((item) => {
            const selected = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={cn(
                  "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
                  selected
                    ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
                    : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
                )}
                onClick={() => setTab(item.id)}
              >
                {item.label}{" "}
                <span className="tabular-nums text-slate-500">{item.count}</span>
              </button>
            );
          })}
        </div>

        {tab === "ncrs" ? (
          <div className="space-y-4">
            <QualityEventNcrTable ncrs={ncrs} />
            {props.writesAllowed ? (
              <div className="flex flex-wrap gap-2">
                <LinkedDocPopdown
                  id="link-event-ncr"
                  label="Link existing NCR"
                  placeholder="NCR id"
                  value={props.ncrId}
                  onChange={props.onNcrIdChange}
                  submitLabel="Link"
                  busy={props.ncrBusy}
                  open={action === "ncr-link"}
                  onToggle={() => setAction((current) => (current === "ncr-link" ? null : "ncr-link"))}
                  onSubmit={props.onLinkNcr}
                />
                <LinkedDocPopdown
                  id="create-event-ncr"
                  label="New NCR"
                  placeholder="What failed?"
                  value={props.ncrIssue}
                  onChange={props.onNcrIssueChange}
                  submitLabel="Create"
                  busy={props.ncrBusy}
                  open={action === "ncr-create"}
                  onToggle={() => setAction((current) => (current === "ncr-create" ? null : "ncr-create"))}
                  onSubmit={props.onCreateNcr}
                />
              </div>
            ) : null}
            {props.ncrError ? <p className="text-sm text-red-700">{props.ncrError}</p> : null}
          </div>
        ) : null}

        {tab === "cases" ? (
          <div className="space-y-4">
            {props.event.qualityCaseId == null ? (
              <p className="text-sm text-slate-600">Not linked to a quality case.</p>
            ) : (
              <LinkedRecordTable columns={["Case", "Title", "Status"]}>
                <tr className="hover:bg-slate-50/80">
                  <td className={linkedCellClass}>
                    <QualityCaseLink caseId={props.event.qualityCaseId} />
                  </td>
                  <td className={linkedCellClass}>{props.event.qualityCaseTitle ?? EM_DASH}</td>
                  <td className={linkedCellClass}>{labeledStatus(props.event.qualityCaseStatus, QUALITY_CASE_STATUSES, qualityCaseStatusLabel)}</td>
                </tr>
              </LinkedRecordTable>
            )}
            {props.writesAllowed ? (
              <div className="flex flex-wrap gap-2">
                <LinkedDocPopdown
                  id="link-event-case"
                  label="Link existing quality case"
                  placeholder="Quality case id"
                  value={props.caseId}
                  onChange={props.onCaseIdChange}
                  submitLabel="Link"
                  busy={props.caseBusy}
                  open={action === "case-link"}
                  onToggle={() => setAction((current) => (current === "case-link" ? null : "case-link"))}
                  onSubmit={props.onLinkCase}
                />
                <LinkedDocPopdown
                  id="create-event-case"
                  label="New quality case"
                  placeholder="Case title"
                  value={props.caseTitle}
                  onChange={props.onCaseTitleChange}
                  submitLabel="Create"
                  busy={props.caseBusy}
                  open={action === "case-create"}
                  onToggle={() => setAction((current) => (current === "case-create" ? null : "case-create"))}
                  onSubmit={props.onCreateCase}
                />
              </div>
            ) : null}
            {props.caseError ? <p className="text-sm text-red-700">{props.caseError}</p> : null}
          </div>
        ) : null}

        {tab === "rcas" ? (
          rcas.length === 0 ? (
            <p className="text-sm text-slate-600">No RCAs linked to this event.</p>
          ) : (
            <LinkedRecordTable columns={["RCA", "Status", "Problem", "Opened"]}>
              {rcas.map((rca) => (
                <tr key={rca.id} className="hover:bg-slate-50/80">
                  <td className={linkedCellClass}>
                    <Link className="text-sky-700 hover:underline" to={`/quality/rcas/${rca.id}`}>
                      {rca.id}
                    </Link>
                  </td>
                  <td className={linkedCellClass}>{plainStatus(rca.status)}</td>
                  <td className={linkedCellClass}>{rca.problemStatement ?? EM_DASH}</td>
                  <td className={linkedCellClass}>{formatTimestamp(rca.createdAt)}</td>
                </tr>
              ))}
            </LinkedRecordTable>
          )
        ) : null}

        {tab === "cars" ? (
          cars.length === 0 ? (
            <p className="text-sm text-slate-600">No corrective actions linked to this event.</p>
          ) : (
            <LinkedRecordTable columns={["CAR", "Title", "Status", "Source", "Opened"]}>
              {cars.map((car) => (
                <tr key={car.id} className="hover:bg-slate-50/80">
                  <td className={linkedCellClass}>
                    <Link className="text-sky-700 hover:underline" to={`/quality/corrective-actions/${car.id}`}>
                      {car.carNumber?.trim() || correctiveActionLabel(car.id)}
                    </Link>
                  </td>
                  <td className={linkedCellClass}>{car.title ?? EM_DASH}</td>
                  <td className={linkedCellClass}>
                    {labeledStatus(car.status, CORRECTIVE_ACTION_STATUSES, correctiveActionStatusLabel)}
                  </td>
                  <td className={linkedCellClass}>{plainStatus(car.sourceType)}</td>
                  <td className={linkedCellClass}>{formatTimestamp(car.openedAt)}</td>
                </tr>
              ))}
            </LinkedRecordTable>
          )
        ) : null}

        {tab === "rmas" ? (
          <div className="space-y-4">
            {rmas.length === 0 ? (
              <p className="text-sm text-slate-600">No RMAs linked to this event.</p>
            ) : (
              <LinkedRecordTable columns={["RMA", "Status"]}>
                {rmas.map((rma) => (
                  <tr key={rma.id} className="hover:bg-slate-50/80">
                    <td className={linkedCellClass}>
                      <Link className="text-sky-700 hover:underline" to={`/quality/rmas/${rma.id}`}>
                        {rma.rmaNumber}
                      </Link>
                    </td>
                    <td className={linkedCellClass}>{labeledStatus(rma.status, RMA_STATUSES, rmaStatusLabel)}</td>
                  </tr>
                ))}
              </LinkedRecordTable>
            )}
            {props.writesAllowed ? (
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={props.onOpenRma}>
                  Open RMA
                </Button>
                <LinkedDocPopdown
                  id="link-event-rma"
                  label="Link existing RMA"
                  placeholder="RMA number"
                  value={props.rmaNumber}
                  onChange={props.onRmaNumberChange}
                  submitLabel="Link"
                  busy={props.rmaBusy}
                  open={action === "rma-link"}
                  onToggle={() => setAction((current) => (current === "rma-link" ? null : "rma-link"))}
                  onSubmit={props.onLinkRma}
                />
              </div>
            ) : null}
            {props.rmaError ? <p className="text-sm text-red-700">{props.rmaError}</p> : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

const linkedHeadClass = "sticky top-0 border-b border-slate-200 bg-slate-50 px-2 py-2";
const linkedCellClass = "border-b border-slate-100 px-2 py-2";

function LinkedDocPopdown(props: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  submitLabel: string;
  busy: boolean;
  open: boolean;
  onToggle: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="relative">
      <Button type="button" variant="secondary" aria-expanded={props.open} onClick={props.onToggle}>
        {props.label}
      </Button>
      {props.open ? (
        <form
          className="absolute left-0 top-full z-20 mt-1 flex w-80 gap-2 rounded-md border border-slate-200 bg-white p-3 shadow-md"
          onSubmit={(event) => {
            event.preventDefault();
            props.onSubmit();
          }}
        >
          <Input
            id={props.id}
            value={props.value}
            placeholder={props.placeholder}
            aria-label={props.placeholder}
            onChange={(event) => props.onChange(event.target.value)}
          />
          <Button type="submit" variant="secondary" disabled={props.busy || props.value.trim() === ""}>
            {props.submitLabel}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

function LinkedRecordTable({ columns, children }: { columns: string[]; children: ReactNode }) {
  return (
    <div className="overflow-auto">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {columns.map((column) => (
              <th key={column} className={linkedHeadClass}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function plainStatus(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return EM_DASH;
  return value
    .split("_")
    .filter((part) => part !== "")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function labeledStatus<T extends string>(
  value: string | null | undefined,
  options: readonly T[],
  label: (value: T) => string,
): string {
  if (value == null || value.trim() === "") return EM_DASH;
  const match = options.find((option) => option === value);
  return match == null ? plainStatus(value) : label(match);
}
